import classNames from 'classnames';
import { Spinner, TextInput } from 'flowbite-react';
import { Popover, PopoverOrigin, PopoverProps } from '@mui/material';
import React, { ChangeEvent, ReactNode, useEffect } from 'react';
import { IoChevronDown } from 'react-icons/io5';
import { useThemeContext } from 'src/context/ThemeProvider';
import { TbSearch } from 'react-icons/tb';

const FilterSelect = ({
	placeholder = 'Select',
	onClose,
	children,
	active,
	arrow,
	anchorOrigin,
	popOverProps,
	transformOrigin,
	maxWidth = 'auto',
	maxHeight = 'auto',
	showSearch = false,
	onSearchChange = e => {},
	isLoading = false,
	onScrollToBottom,
}: {
	placeholder?: string | ReactNode;
	onClose?: () => void;
	children: ReactNode | ReactNode[] | ((props: any) => ReactNode | ReactNode[]);
	active?: boolean;
	arrow?: boolean;
	maxHeight?: string;
	maxWidth?: string;
	showSearch?: boolean;
	anchorOrigin?: PopoverOrigin;
	transformOrigin?: PopoverOrigin;
	popOverProps?: PopoverProps;
	isLoading?: boolean;
	onScrollToBottom?: () => void;
	onSearchChange?: (event: ChangeEvent<HTMLInputElement>) => void;
}) => {
	const { themeMode } = useThemeContext();
	const searchRef = React.useRef<HTMLInputElement>(null);
	const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
	const contentRef = React.useRef<HTMLDivElement>(null);

	const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handlePopoverClose = () => {
		setAnchorEl(null);
		onClose && onClose();
	};

	const open = Boolean(anchorEl);

	useEffect(() => {
		const handleScroll = () => {
			if (contentRef.current && onScrollToBottom) {
				const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
				if (scrollHeight - scrollTop <= clientHeight + 1) {
					onScrollToBottom();
				}
			}
		};

		const currentContentRef = contentRef.current;

		if (open && currentContentRef) {
			currentContentRef.addEventListener('scroll', handleScroll);
		}

		return () => {
			if (currentContentRef) {
				currentContentRef.removeEventListener('scroll', handleScroll);
			}
		};
	}, [open, onScrollToBottom]);

	useEffect(() => {
		if (open && showSearch) {
			// Use a small timeout to ensure the Popover content is rendered
			const timeoutId = setTimeout(() => {
				searchRef.current?.focus();
			}, 100); // 100ms delay, adjust if needed

			// Clean up the timeout to avoid memory leaks
			return () => clearTimeout(timeoutId);
		}
	}, [open, showSearch]);

	return (
		<div className="w-full">
			<button
				onClick={handlePopoverOpen}
				className={classNames(
					'text-left border dark:border-[#3F3F3F] bg-white dark:bg-[#1F2A37] rounded-[8px] px-3 py-2 flex gap-2 justify-between items-center w-full',
					{
						'text-[#555770] border-[#555770] ': typeof active === 'boolean' && active,
						'text-[#8F90A6]': typeof active === 'boolean' && !active,
					},
				)}
			>
				<div className="text-sm font-[400]">{placeholder}</div>
				<div>
					<IoChevronDown
						color="#C7C9D9"
						className={classNames('h-[18px] w-[18px] transition-all', open ? 'rotate-[180deg]' : '')}
					/>
				</div>
			</button>

			<div className="relative flex justify-center">
				{/* {arrow ? (
					<IoMdArrowDropup
						className={classNames(
							'absolute top-[-10px] mx-auto w-fit h-[30px] text-[#E5E7EB] dark:text-[#374151] transition-all',
							open ? 'scale-100' : 'scale-0',
						)}
					/>
				) : null} */}
				<Popover
					id={'id'}
					anchorEl={anchorEl}
					disablePortal={true}
					onClose={handlePopoverClose}
					anchorOrigin={
						anchorOrigin
							? anchorOrigin
							: {
									vertical: 'bottom',
									horizontal: 'center',
							  }
					}
					sx={{
						'& .MuiPopover-paper': {
							boxShadow: 'none',
							borderRadius: '8px',
							backgroundColor: themeMode === 'light' ? 'white' : '#1F2A37',
							border: themeMode === 'light' ? '1px solid #E5E7EB' : '1px solid #374151',
							marginTop: '8px',
							maxHeight: maxHeight,
							maxWidth: maxWidth,
						},
					}}
					transformOrigin={
						transformOrigin
							? transformOrigin
							: {
									vertical: 'top',
									horizontal: 'center',
							  }
					}
					{...(popOverProps || {})}
					open={open}
				>
					<div ref={contentRef} className="bg-inherit" style={{ maxHeight: maxHeight, overflowY: 'auto' }}>
						{showSearch && (
							<div className="sticky top-0 bg-inherit z-[1] p-3">
								<TextInput
									name="search"
									ref={searchRef}
									placeholder="Search"
									onChange={onSearchChange}
									icon={() => <TbSearch color="#8b8d9a" />}
									rightIcon={() => (isLoading ? <Spinner /> : null)}
									className="w-full [&_input]:bg-white transition-all"
								/>
							</div>
						)}
						{typeof children === 'function' ? children({ handlePopoverClose }) : children}
					</div>
				</Popover>
			</div>
		</div>
	);
};

export default FilterSelect;
